import { Link } from 'react-router-dom';
import { CalendarClock, ChevronRight, ScrollText, Scale, Workflow } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { StatusPill, type CaseStatus as UIStatus } from '@/components/ui/StatusPill';
import { ptBR } from '@/i18n/pt-BR';
import type { CaseRecordData } from '@/lib/cases';
import { targetPathForCase, type DeadlineEntry } from '@/lib/home_panel';

const BACKEND_STATUS_TO_UI: Record<CaseRecordData['status'], UIStatus> = {
  draft: 'rascunho',
  analysing: 'em_analise',
  done: 'concluido',
  conflict: 'aguardando_revisao',
};

const CASE_STATUS_LABEL: Record<CaseRecordData['status'], string> = {
  draft: ptBR.casos.statusDraft,
  analysing: ptBR.casos.statusAnalysing,
  done: ptBR.casos.statusDone,
  conflict: ptBR.casos.statusConflict,
};

function deadlineLabel(daysAway: number): string {
  if (daysAway < 0) return ptBR.homePanel.prazoOverdue;
  if (daysAway === 0) return ptBR.homePanel.prazoToday;
  if (daysAway === 1) return ptBR.homePanel.prazoTomorrow;
  return ptBR.homePanel.prazoDaysAway.replace('{{days}}', String(daysAway));
}

export function PanelSkeleton() {
  return (
    <div className="mt-6 grid gap-4" data-testid="home-panel-loading">
      <Skeleton className="h-24 w-full" />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
      </div>
      <Skeleton className="h-40" />
    </div>
  );
}

export function ContinuarSection({ items }: { items: readonly CaseRecordData[] }) {
  if (items.length === 0) {
    return (
      <Card data-testid="home-continuar-empty">
        {/* Sem `CardHeader`, então o respiro de cima tem de vir daqui — ver a
            nota no `KpiCard`. */}
        <CardContent className="pt-sc-6">
          <p className="text-body-sm text-fg-muted">{ptBR.homePanel.continuarEmpty}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid="home-continuar">
      <CardHeader>
        <CardTitle>{ptBR.homePanel.continuarTitle}</CardTitle>
        <p className="text-caption text-fg-muted">{ptBR.homePanel.continuarHint}</p>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col divide-y divide-border">
          {items.map((c) => (
            <li key={c.id}>
              <Link
                to={targetPathForCase(c)}
                data-testid={`home-continuar-item-${c.display_code}`}
                aria-label={ptBR.homePanel.openCaseLabel.replace('{{code}}', c.display_code)}
                className="flex items-center justify-between gap-3 py-3 hover:bg-fg/[0.04]"
              >
                <div className="min-w-0">
                  <p className="truncate text-body-md text-fg">{c.title}</p>
                  <p className="font-mono text-caption text-fg-muted">{c.display_code}</p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusPill
                    status={BACKEND_STATUS_TO_UI[c.status]}
                    label={CASE_STATUS_LABEL[c.status]}
                  />
                  <ChevronRight className="h-4 w-4 text-fg-muted" aria-hidden="true" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function PrazosSection({ entries }: { entries: readonly DeadlineEntry[] }) {
  if (entries.length === 0) {
    return (
      <Card data-testid="home-prazos-empty">
        <CardHeader>
          <CardTitle>{ptBR.homePanel.prazosTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-body-sm text-fg-muted">{ptBR.homePanel.prazosEmpty}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid="home-prazos">
      <CardHeader>
        <CardTitle>{ptBR.homePanel.prazosTitle}</CardTitle>
        <p className="text-caption text-fg-muted">{ptBR.homePanel.prazosHint}</p>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col divide-y divide-border">
          {entries.map(({ case: c, daysAway }) => (
            <li
              key={c.id}
              className="flex items-center justify-between gap-3 py-3"
              data-testid={`home-prazo-item-${c.display_code}`}
            >
              <div className="min-w-0">
                <Link
                  to={targetPathForCase(c)}
                  className="truncate text-body-md text-fg hover:text-accent-700"
                >
                  {c.title}
                </Link>
                <p className="font-mono text-caption text-fg-muted">{c.display_code}</p>
              </div>
              <span
                className={`flex items-center gap-1 text-caption font-semibold uppercase tracking-wide ${
                  daysAway < 0 ? 'text-danger-700' : 'text-fg-muted'
                }`}
                data-testid={`home-prazo-label-${c.display_code}`}
              >
                <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
                {deadlineLabel(daysAway)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

interface Feature {
  Icon: typeof Workflow;
  title: string;
  body: string;
}

const FEATURES: readonly Feature[] = [
  { Icon: Workflow, title: ptBR.home.feature1Title, body: ptBR.home.feature1Body },
  { Icon: Scale, title: ptBR.home.feature2Title, body: ptBR.home.feature2Body },
  { Icon: ScrollText, title: ptBR.home.feature3Title, body: ptBR.home.feature3Body },
];

export function EducationalFooter() {
  return (
    <details className="mt-10 border border-border bg-fg/[0.03]" data-testid="home-educational">
      <summary className="cursor-pointer list-none px-4 py-3 text-body-sm font-semibold text-fg hover:bg-fg/[0.07]">
        {ptBR.homePanel.educationalToggle}
      </summary>
      <div className="grid grid-cols-1 gap-4 border-t border-border p-4 sm:grid-cols-3">
        {FEATURES.map((feature) => (
          <div key={feature.title} className="border border-border bg-surface p-4 text-left">
            <feature.Icon className="h-5 w-5 text-accent-600" aria-hidden="true" />
            <h3 className="mt-3 text-heading-sm text-fg">{feature.title}</h3>
            <p className="mt-1 text-body-sm text-fg-muted">{feature.body}</p>
          </div>
        ))}
      </div>
    </details>
  );
}

export { FEATURES };
